import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';

import { CameraService } from '../../service/camera.service';
import { ICamera } from '../../model/camera.model';
import { VideoProcessingService } from '../../service/videoProcessing.service';

// Helper function to get filename without extension
const getBaseName = (fileName: string): string => {
    // Find the last dot
    const lastDotIndex = fileName.lastIndexOf('.');
    // If no dot, or dot is the first character, return the whole name
    if (lastDotIndex < 1) return fileName;
    // Otherwise, return the substring before the last dot
    return fileName.substring(0, lastDotIndex);
};

const VideoInspektorView: React.FC = () => {
  const navigate = useNavigate();
  const [originalVideoFile, setOriginalVideoFile] = useState<File | null>(null);
  const [originalVideoUrl, setOriginalVideoUrl] = useState<string | null>(null);
  const [modifiedVideoUrl, setModifiedVideoUrl] = useState<string | null>(null); // Simulate: Replace with actual URL from backend
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false); // State for drag-n-drop visual feedback
  const [selectedCamera, setSelectedCamera] = useState<string>(''); // State for selected camera ID
  const [cameras, setCameras] = useState<ICamera[]>([]); // State for fetched cameras
  const [isLoadingCameras, setIsLoadingCameras] = useState<boolean>(true); // Loading state
  const [cameraError, setCameraError] = useState<string | null>(null); // Error state

  const originalVideoRef = useRef<HTMLVideoElement>(null);
  const modifiedVideoRef = useRef<HTMLVideoElement>(null);

  // Effect to fetch cameras on mount
  useEffect(() => {
    const fetchCameras = async () => {
      setIsLoadingCameras(true);
      setCameraError(null);
      try {
        const fetchedCameras = await CameraService.getAllCameras();
        setCameras(fetchedCameras);
      } catch (error) {
        console.error("Fehler beim Laden der Kameras:", error);
        setCameraError('Kameras konnten nicht geladen werden.');
      } finally {
        setIsLoadingCameras(false);
      }
    };

    fetchCameras();
  }, []); // Empty dependency array ensures this runs only once on mount

  // Reusable function to process the selected/dropped file
  const processFile = (file: File | null | undefined) => {
    if (file && file.type === 'video/mp4') {
      setOriginalVideoFile(file);
      const url = URL.createObjectURL(file);
      // Revoke previous URL if exists to prevent memory leaks
      if (originalVideoUrl) {
        URL.revokeObjectURL(originalVideoUrl);
      }
      setOriginalVideoUrl(url);
      setModifiedVideoUrl(null); // Reset modified video on new upload
      setIsPlaying(false); // Reset playing state
    } else {
      if (file) { // Only alert if a file was actually provided but invalid
         alert('Please upload/drop a valid MP4 file.');
      }
      // Don't reset if the file is null/undefined (e.g., drag leave without drop)
      // setOriginalVideoFile(null);
      // setOriginalVideoUrl(null);
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    processFile(file);
    // Reset file input value to allow uploading the same file again if needed
    event.target.value = '';
  };

  const handleProcessVideo = async () => {
      if (!originalVideoFile || !selectedCamera) {
        alert('Bitte laden Sie ein MP4-Video hoch und wählen Sie eine Kamera aus.');
        return;
      }
      setIsProcessing(true);
      setCameraError(null); // Clear previous errors

      // Revoke previous modified URL if it exists
      if (modifiedVideoUrl && modifiedVideoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(modifiedVideoUrl);
      }

      setModifiedVideoUrl(null); // Clear previous result visually
      console.log('Requesting video correction for camera:', selectedCamera);
      console.log('Original file:', originalVideoFile!.name);

      try {
         // --- Call the actual API Service --- 
         const correctedVideoBlob = await VideoProcessingService.correctVideo(
           originalVideoFile!,
           selectedCamera
         );

         // --- Create Object URL from the *returned* Blob --- 
         const correctedVideoUrl = URL.createObjectURL(correctedVideoBlob);
         setModifiedVideoUrl(correctedVideoUrl);
         console.log('Video correction successful.');

      } catch (error: any) {
         console.error('Video correction failed:', error);
         // Display the error message from the service
         setCameraError(error.message || 'Ein unbekannter Fehler ist bei der Videokorrektur aufgetreten.'); 
         // Optionally, display this error more prominently in the UI
         alert(`Fehler bei der Videokorrektur: ${error.message || 'Unbekannter Serverfehler'}`);
      } finally {
         setIsProcessing(false);
         setIsPlaying(false); // Ensure video is paused after processing completes or fails
      }
  };

  const handlePlay = () => {
    originalVideoRef.current?.play();
    // Only play the modified video if its ref exists and has a source
    if (modifiedVideoRef.current && modifiedVideoRef.current.currentSrc) {
        modifiedVideoRef.current?.play();
    }
    setIsPlaying(true);
  };

  const handlePause = () => {
    originalVideoRef.current?.pause();
    // Only pause the modified video if its ref exists and has a source
    if (modifiedVideoRef.current && modifiedVideoRef.current.currentSrc) {
        modifiedVideoRef.current?.pause();
    }
    setIsPlaying(false);
  };

  // Function to reset videos to the start
  const handleReset = () => {
    if (originalVideoRef.current) {
        originalVideoRef.current.currentTime = 0;
        originalVideoRef.current.pause();
    }
    if (modifiedVideoRef.current && modifiedVideoRef.current.currentSrc) {
        modifiedVideoRef.current.currentTime = 0;
        modifiedVideoRef.current.pause();
    }
    setIsPlaying(false);
  };

  // Clean up object URLs on component unmount
  React.useEffect(() => {
    return () => {
      if (originalVideoUrl) {
        URL.revokeObjectURL(originalVideoUrl);
      }
      // Assuming modifiedVideoUrl might also be an object URL if generated client-side in some scenarios
      // If it's always a server URL, this cleanup might not be needed for it.
      if (modifiedVideoUrl && modifiedVideoUrl.startsWith('blob:')) {
         URL.revokeObjectURL(modifiedVideoUrl);
      }
    };
  }, [originalVideoUrl, modifiedVideoUrl]);

  // Drag and Drop Handlers
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault(); // Necessary to allow dropping
    event.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
      event.dataTransfer.clearData(); // Recommended practice
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
        <MuiLink
          color="inherit"
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Dashboard
        </MuiLink>
        <MuiLink
          color="inherit"
          component="button"
          variant="body1"
          onClick={() => navigate('/backoffice')}
          sx={{ textDecoration: 'none', cursor: 'pointer' }}
        >
          Administration
        </MuiLink>
        <Typography color="text.primary">Videoinspektor</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            sx={{ mr: 2 }}
            onClick={() => navigate('/backoffice')}
            aria-label="Zurück zur Administration"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Videoinspektor
          </Typography>
        </Box>
      </Box>

      {/* Content Area */}
      <Grid container spacing={4} alignItems="center">

        {/* 1. Upload Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              border: `2px dashed ${isDraggingOver ? 'primary.main' : 'grey'}`,
              p: 3,
              textAlign: 'center',
              backgroundColor: isDraggingOver ? 'action.hover' : 'transparent',
              transition: 'border-color 0.3s ease, background-color 0.3s ease',
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Typography variant="h6" gutterBottom>
              1. MP4-Video hochladen (oder hierher ziehen)
            </Typography>
            <Button variant="contained" component="label">
              Datei auswählen (.mp4)
              <input
                type="file"
                hidden
                accept="video/mp4"
                onChange={handleFileChange}
                onClick={(e) => (e.target as HTMLInputElement).value = ''} // Allow selecting same file again
              />
            </Button>
            {originalVideoFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Ausgewählt: {originalVideoFile.name}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* 2. Camera Selection Dropdown */}
        <Grid item xs={12}> {/* Make dropdown full width for better spacing */}
            <FormControl fullWidth variant="outlined" sx={{ mt: 2 }} disabled={isLoadingCameras || isProcessing || !originalVideoFile || !!cameraError}>
                <InputLabel id="camera-select-label">2. Kamera auswählen</InputLabel>
                <Select
                    labelId="camera-select-label"
                    id="camera-select"
                    value={selectedCamera}
                    label="2. Kamera auswählen" // Updated label
                    onChange={(e) => setSelectedCamera(e.target.value as string)}
                >
                    {isLoadingCameras && <MenuItem value=""><em>Laden...</em></MenuItem>}
                    {cameraError && <MenuItem value="" disabled><em>{cameraError}</em></MenuItem>}
                    {!isLoadingCameras && !cameraError && cameras.length === 0 && <MenuItem value="" disabled><em>Keine Kameras gefunden</em></MenuItem>}
                    {!isLoadingCameras && !cameraError && cameras.length > 0 &&
                      <MenuItem value="">
                        <em>Bitte auswählen</em>
                      </MenuItem>
                    }
                    {cameras.map((camera) => (
                        <MenuItem key={camera.ref || camera.displayName} value={camera.ref || ''}> {/* Use ref as ID, fallback key */}
                            {camera.displayName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Grid>

        {/* 3. Video Display Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Original</Typography>
          <video
            ref={originalVideoRef}
            src={originalVideoUrl || ''}
            controls
            width="100%"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            loop
            style={{
              backgroundColor: '#000',
              aspectRatio: '728.01 / 412.35' // Set aspect ratio
            }}
            muted // Mute one video by default if needed to avoid echo
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Modifiziert</Typography>
          <video
            ref={modifiedVideoRef}
            src={modifiedVideoUrl || ''}
            controls
            width="100%"
            loop
            style={{
              backgroundColor: '#000',
              aspectRatio: '728.01 / 412.35' // Set aspect ratio
            }}
          />
        </Grid>

        {/* 4. Combined Controls Section */}
        <Grid item xs={12}>
           <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                 {!isPlaying ? (
                    <Button
                        variant="contained"
                        sx={{ minWidth: '120px' }}
                        startIcon={<PlayArrowIcon />}
                        onClick={handlePlay}
                        disabled={!originalVideoUrl || isProcessing}
                    >
                        Abspielen
                    </Button>
                 ) : (
                    <Button
                        variant="contained"
                        sx={{ minWidth: '120px' }}
                        startIcon={<PauseIcon />}
                        onClick={handlePause}
                        disabled={!originalVideoUrl || isProcessing}
                    >
                        Pause
                    </Button>
                 )}
                 <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={handleReset}
                    disabled={!originalVideoUrl || isProcessing}
                 >
                    Zurücksetzen
                 </Button>
                 <Button
                     variant="contained"
                     color="primary"
                     onClick={handleProcessVideo}
                     disabled={!originalVideoFile || isProcessing || !selectedCamera}
                     sx={{ minWidth: '180px' }} // Ensure width consistency
                 >
                    Korrektur vornehmen
                 </Button>
                 <Button
                     variant="contained"
                     color="primary"
                     startIcon={<DownloadIcon />}
                     href={modifiedVideoUrl || '#'} // Provide a fallback href
                     download={originalVideoFile ? `modified_${getBaseName(originalVideoFile.name)}.mp4` : 'modified_video.mp4'}
                     disabled={!modifiedVideoUrl} // Disable if no modified URL
                     sx={{ minWidth: '180px' }} // Consistent width
                 >
                    Herunterladen
                 </Button>
                 {isProcessing && <CircularProgress size={24} sx={{ ml: 1 }} />}
              </Box>
          </Grid>

      </Grid>
    </Container>
  );
};

export default VideoInspektorView; 